import React, { useEffect, useRef, useState } from 'react';
import molecularService from '../../services/molecularService';

const THREEDMOL_CDN = 'https://3Dmol.csb.pitt.edu/build/3Dmol-min.js';

/**
 * Lazy-load the 3Dmol library from its CDN exactly once per page.
 * Returns a Promise that resolves to the global `$3Dmol` object.
 */
function load3Dmol() {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.$3Dmol) return Promise.resolve(window.$3Dmol);
  if (window.__threeDmolLoading) return window.__threeDmolLoading;

  window.__threeDmolLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = THREEDMOL_CDN;
    script.async = true;
    script.onload = () => resolve(window.$3Dmol);
    script.onerror = () => reject(new Error('Failed to load 3Dmol from CDN'));
    document.head.appendChild(script);
  });

  return window.__threeDmolLoading;
}

/**
 * MolecularViewer
 *
 * Renders a 3D protein structure for a given UniProt accession using
 * AlphaFold predictions served via our backend proxy. Confidence
 * (pLDDT) coloring is applied so users see which regions are reliable.
 *
 * Props:
 *   - uniprotId (string, required)
 *   - height (number, optional, default 360)
 */
const MolecularViewer = ({ uniprotId, height = 360 }) => {
  const containerRef = useRef(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uniprotId) return undefined;

    let viewer = null;
    let cancelled = false;

    const render = async () => {
      setLoading(true);
      setError(null);
      try {
        const [$3Dmol, meta, pdbResponse] = await Promise.all([
          load3Dmol(),
          molecularService.getStructureMetadata(uniprotId),
          fetch(molecularService.getPdbProxyUrl(uniprotId)).then((res) => {
            if (!res.ok) throw new Error(`PDB fetch failed (${res.status})`);
            return res.text();
          }),
        ]);

        if (cancelled || !containerRef.current) return;

        setMetadata(meta);
        containerRef.current.innerHTML = '';

        viewer = $3Dmol.createViewer(containerRef.current, {
          backgroundColor: '#0b1220',
        });
        viewer.addModel(pdbResponse, 'pdb');
        viewer.setStyle({}, {
          cartoon: { colorscheme: { prop: 'b', gradient: 'roygb', min: 50, max: 90 } },
        });
        viewer.zoomTo();
        viewer.render();
      } catch (err) {
        if (!cancelled) {
          console.error('MolecularViewer error:', err);
          setError(err.response?.data?.detail || err.message || 'Error cargando estructura');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    render();

    return () => {
      cancelled = true;
      if (viewer && typeof viewer.clear === 'function') {
        try { viewer.clear(); } catch (_) { /* noop */ }
      }
    };
  }, [uniprotId]);

  return (
    <div style={styles.wrapper}>
      <div
        ref={containerRef}
        style={{ ...styles.canvas, height: `${height}px` }}
        aria-label={`Estructura 3D de ${uniprotId}`}
      />

      {loading && (
        <div style={styles.overlay}>
          <div style={styles.spinner} />
          <p style={styles.overlayText}>Cargando estructura {uniprotId}…</p>
        </div>
      )}

      {error && !loading && (
        <div style={styles.overlay}>
          <p style={styles.errorText}>{error}</p>
        </div>
      )}

      {metadata && !loading && !error && (
        <div style={styles.metadataBar}>
          <strong>{metadata.uniprot_description || metadata.uniprot_id}</strong>
          {metadata.organism_scientific_name && (
            <span style={styles.metaSpan}>{metadata.organism_scientific_name}</span>
          )}
          {metadata.sequence_length && (
            <span style={styles.metaSpan}>{metadata.sequence_length} aa</span>
          )}
          {typeof metadata.global_metric_value === 'number' && (
            <span style={styles.metaSpan}>
              pLDDT promedio: {metadata.global_metric_value.toFixed(1)}
            </span>
          )}
          <span style={styles.attribution}>
            Predicción de AlphaFold (EBI · DeepMind)
          </span>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrapper: {
    position: 'relative',
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #1f2937',
    background: '#0b1220',
  },
  canvas: {
    width: '100%',
    minHeight: 320,
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(11, 18, 32, 0.85)',
    color: '#e5e7eb',
    fontSize: 14,
  },
  overlayText: {
    marginTop: 12,
  },
  errorText: {
    color: '#fca5a5',
    padding: 16,
    textAlign: 'center',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid rgba(255,255,255,0.2)',
    borderTopColor: '#10b981',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  metadataBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '8px 12px',
    background: 'rgba(11, 18, 32, 0.85)',
    color: '#e5e7eb',
    fontSize: 12,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  metaSpan: {
    color: '#9ca3af',
  },
  attribution: {
    marginLeft: 'auto',
    color: '#6ee7b7',
    fontStyle: 'italic',
  },
};

export default MolecularViewer;
